import { createFetchRetry } from '@rsos/capstone';
import { mapLayerSettingsSelector } from '@rsos/reduxDataStore';
import { getTokens } from '@rsos/sinatra';
import { checkStatus, parseJSON } from '@rsos/utils';
import { getAPIHost } from '@rsos/utils/metaTags';
import serializeRequestError from '@rsos/utils/serializeRequestError';
import { normalizePhone } from '../components/JurisdictionView/AviveAED/aviveSlice';
import {
  AVIVE2_STATUS_AED_CANCELED,
  AVIVE2_STATUS_AED_CONCLUDED,
  AVIVE2_STATUS_EMERGENCY_TIMEOUT,
} from '../components/JurisdictionView/AviveAED/aviveStatusTypes';
import {
  ADD_DYNAMIC_LAYER,
  CLEAR_CHECKED_LAYERS,
  DEACTIVATE_SPATIAL_LAYER,
  FETCH_CRG_DEEPLINK_START,
  FETCH_CRG_DEEPLINK_SUCCESS,
  FETCH_CRG_DEEPLINK_FAILED,
  FORCE_STATIC_LAYER_ON,
  GET_SPATIAL_LAYERS_START,
  GET_SPATIAL_LAYERS_SUCCESS,
  GET_SPATIAL_LAYERS_FAILED,
  GET_LATEST_ACTIVE_SPATIAL_LAYERS,
  SET_ACTIVE_CRG_OBJECT,
  INACTIVATE_AVIVE_REQUEST,
  SET_ACTIVE_CRG_OBJECT_FLOOR,
  SET_STATIC_SPATIAL_OBJECT_ACTIVE_STATUS,
  SET_STATIC_SPATIAL_OBJECT_INACTIVE_STATUS,
  TOGGLE_SPATIAL_LAYER,
  TOGGLE_SPATIAL_LAYER_GROUP,
  UPDATE_DYNAMIC_LAYER,
  UPDATE_DISABLED_DYNAMIC_LAYERS,
} from './actionTypes';

const baseURL = `${getAPIHost()}`;

export const addDynamicLayer = layer => ({
  type: ADD_DYNAMIC_LAYER,
  layer,
});

export const fetchSpatialLayersStart = () => ({
  type: GET_SPATIAL_LAYERS_START,
});

export const fetchSpatialLayersSuccess = allSpatialLayers => ({
  type: GET_SPATIAL_LAYERS_SUCCESS,
  allSpatialLayers,
});

export const fetchSpatialLayersFailed = error => ({
  type: GET_SPATIAL_LAYERS_FAILED,
  error,
});

export const clearCheckedLayers = () => ({
  type: CLEAR_CHECKED_LAYERS,
});

export const deactivateSpatialLayerStatus = inActiveID => {
  return {
    type: DEACTIVATE_SPATIAL_LAYER,
    inActiveID,
  };
};

/**
 * Fetches all spatial-layers.
 */
export const fetchSpatialLayers = orgId => (dispatch, getState) => {
  dispatch(fetchSpatialLayersStart());

  const fetchRetry = createFetchRetry({ getState });

  return fetchRetry(`${baseURL}/v1/spatial-layers/portal/${orgId}`)
    .then(checkStatus)
    .then(res => parseJSON(res))
    .then(res => {
      const layers = res.layers;
      const aviveAEDRequestLayer = layers.find(
        layer => layer.key === 'avive_aed_request',
      );

      // The avive_aed_request layer is a dynamic layer, but it's not aware
      // of its avive status since it arrives separately as ADR. When fetching
      // the spatial layers, we need to pick out the avive_aed_request layer
      // to flag if any of its spatial objects are currently inactive.
      if (aviveAEDRequestLayer) {
        // For each of the objects get the phone number, then use it to
        // look it up in the state.avive slice to see if it's timed out,
        // concluded, or canceled. If it is, then set the inactiveRequest
        // attribute to true.
        const aviveStatuses = getState().avive?.statuses;
        const aviveRequests = aviveAEDRequestLayer.spatial_objects;
        aviveRequests.forEach(req => {
          const phone = normalizePhone(req?.attributes?.phone_number);
          const aviveStatus = aviveStatuses[`tel://${phone}`];
          const status = aviveStatus?.data?.emergency_status_update;

          const AVIVE_RESOLVED_STATUSES = [
            AVIVE2_STATUS_EMERGENCY_TIMEOUT,
            AVIVE2_STATUS_AED_CONCLUDED,
            AVIVE2_STATUS_AED_CANCELED,
          ];

          if (status && AVIVE_RESOLVED_STATUSES.includes(status)) {
            // Set the layer key in the spatial object so it can be picked out
            // in the getPick of the IconLayer drawing the spatial object in
            // DynamicLayerGL.
            req.key = 'avive_aed_request';
            // Add flag so the avive_aed_request spatial object knows it's inactive.
            req.attributes.inactiveRequest = true;
          }
        });
      }

      // PSAPs can choose to enable layers by default (see
      // /admin/data-source/map-layers). If a layer should be on by default, set
      // its isChecked here, and it will be turned on after the layers are
      // loaded into state.
      const settings = mapLayerSettingsSelector(getState());
      layers.forEach(layer => {
        if (settings[layer.id]) {
          layer.isChecked = settings[layer.id].default_checked;
        }
      });
      dispatch(fetchSpatialLayersSuccess(layers));
    })
    .catch(async error => {
      const serializedError = await serializeRequestError(error);
      dispatch(fetchSpatialLayersFailed(serializedError));
    });
};

export const getLatestActiveSpatialLayer = message => ({
  type: GET_LATEST_ACTIVE_SPATIAL_LAYERS,
  message,
});

export const setActiveCRGObject = spatialObject => ({
  type: SET_ACTIVE_CRG_OBJECT,
  spatialObject,
});

export const setActiveCRGObjectFloor = floor => ({
  type: SET_ACTIVE_CRG_OBJECT_FLOOR,
  floor,
});
export const toggleSpatialLayer = (layerKey, isDynamic, isChecked) => ({
  type: TOGGLE_SPATIAL_LAYER,
  layerKey,
  isDynamic,
  isChecked,
});
export const toggleSpatialLayerGroup = layersKeys => ({
  type: TOGGLE_SPATIAL_LAYER_GROUP,
  layersKeys,
});
export const updateDisabledDynamicLayers = (layerKey, isDisabled) => ({
  type: UPDATE_DISABLED_DYNAMIC_LAYERS,
  layerKey,
  isDisabled,
});

export const updateDynamicLayer = layer => ({
  type: UPDATE_DYNAMIC_LAYER,
  layer,
});

export const fetchCRGDeeplinkStart = () => ({
  type: FETCH_CRG_DEEPLINK_START,
});

export const fetchCRGDeeplinkSuccess = data => ({
  type: FETCH_CRG_DEEPLINK_SUCCESS,
  data,
});

export const fetchCRGDeeplinkFailed = error => ({
  type: FETCH_CRG_DEEPLINK_FAILED,
  error,
});

export const fetchCRGDeeplink =
  (ordID, spatialObjectID) => async (dispatch, getState) => {
    dispatch(fetchCRGDeeplinkStart());
    const { token } = getTokens();
    const fetchRetry = createFetchRetry({ getState });
    return fetchRetry(`${baseURL}/v1/deeplink/crg/${ordID}`, {
      method: 'POST',
      body: JSON.stringify({ spatial_object_id: spatialObjectID }),
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
    })
      .then(checkStatus)
      .then(res => parseJSON(res))
      .then(res => {
        dispatch(fetchCRGDeeplinkSuccess(res));
      })
      .catch(async error => {
        const serializedError = await serializeRequestError(error);
        dispatch(fetchCRGDeeplinkFailed(serializedError));
      });
  };

export const forceStaticLayerOn = layerSource => ({
  type: FORCE_STATIC_LAYER_ON,
  layerSource,
});

export const inactivateAviveRequest = phone => ({
  type: INACTIVATE_AVIVE_REQUEST,
  phone,
});

export const setStaticSpatialObjectActiveStatus = payload => ({
  type: SET_STATIC_SPATIAL_OBJECT_ACTIVE_STATUS,
  payload,
});

export const setStaticSpatialObjectInactiveStatus = payload => ({
  type: SET_STATIC_SPATIAL_OBJECT_INACTIVE_STATUS,
  payload,
});
